<template>
	<span v-if="canRecord" class="CopilotMic" :data-state="state" data-cy="copilot-mic">
		<span v-if="error" class="CopilotMic__error" role="alert" data-cy="copilot-mic-error">{{ error }}</span>
		<span v-if="state === 'recording'" class="CopilotMic__time" aria-live="off" data-cy="copilot-mic-time">{{ timeText }}</span>
		<v-progress-circular v-if="state === 'transcribing'" indeterminate size="18" width="2" class="CopilotMic__spinner" data-cy="copilot-mic-spinner" />
		<v-btn
			:icon="state === 'recording' ? 'mdi-stop' : 'mdi-microphone'"
			:color="state === 'recording' ? 'error' : undefined"
			variant="text"
			size="small"
			:class="{ 'CopilotMic__button--pulse': state === 'recording' }"
			:disabled="disabled || state === 'permission' || state === 'transcribing'"
			:aria-pressed="String(state === 'recording')"
			:aria-label="buttonLabel"
			:title="buttonLabel"
			data-cy="copilot-mic-button"
			@click="toggle"
		/>
	</span>
</template>

<script lang="js">
// Voice input for the Copilot composer (AAL-86, ticket 11). MediaRecorder records in the
// browser's own container (webm/Opus in Chrome, Edge, Firefox; mp4 in Safari); Stop hands the
// blob to the `transcribe` prop (the page's client call with the user's token) and the text is
// emitted as `transcript` for the drawer to insert at the cursor. Nothing is sent to the agent
// by this component. Escape cancels a recording (nothing is transcribed); a recording stops by
// itself at `maxSeconds`, with a countdown against the maximum in the last 15 s. Rendered only
// when the browser can record; whether transcription is on is the drawer's decision (features).
// The audio lives in the one request only: no copy is kept after the call.
const COUNTDOWN_S = 15
const TICK_MS = 250

const clock = seconds => `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`

export default {
	name: 'CopilotMic',
	props: {
		/** (blob: Blob) => Promise<string>: posts the audio and resolves with the text */
		transcribe: { type: Function, required: true },
		/** the service's cap (features.transcribe_max_seconds); the recording stops here by itself */
		maxSeconds: { type: Number, default: 120 },
		disabled: { type: Boolean, default: false },
	},
	emits: [ 'transcript' ],
	data: () => ({
		/** idle | permission | recording | transcribing */
		state: 'idle',
		elapsed: 0,
		error: '',
	}),
	computed: {
		canRecord() {
			return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && typeof window.MediaRecorder === 'function'
		},
		timeText() {
			const remaining = this.maxSeconds - this.elapsed
			return remaining <= COUNTDOWN_S ? `${clock(this.elapsed)} / ${clock(this.maxSeconds)}` : clock(this.elapsed)
		},
		buttonLabel() {
			return this.$t({
				idle: 'text.copilotMicDictate',
				permission: 'text.copilotMicPermission',
				recording: 'text.copilotMicStop',
				transcribing: 'text.copilotMicTranscribing',
			}[this.state])
		},
	},
	created() {
		// non-reactive: the browser objects of the recording in progress
		this.recorder = null
		this.stream = null
		this.chunks = []
		this.timer = null
		this.startedAt = 0
		this.cancelled = false
		this.onEscape = event => {
			if (event.key !== 'Escape') return
			event.preventDefault()
			// the drawer's own Escape closes the drawer; a recording swallows the key
			event.stopPropagation()
			event.stopImmediatePropagation()
			this.cancel()
		}
	},
	beforeUnmount() {
		this.cancelled = true
		if (this.recorder?.state === 'recording') this.recorder.stop()
		this.release()
	},
	methods: {
		toggle() {
			if (this.state === 'recording') this.stop()
			else if (this.state === 'idle') this.start()
		},
		async start() {
			this.error = ''
			this.cancelled = false
			this.chunks = []
			this.state = 'permission'
			document.addEventListener('keydown', this.onEscape, true)
			try {
				this.stream = await navigator.mediaDevices.getUserMedia({ audio: true })
			}
			catch (error) {
				this.release()
				this.state = 'idle'
				this.error = this.$t(error?.name === 'NotFoundError' ? 'text.copilotMicErrorNoDevice' : 'text.copilotMicErrorBlocked')
				return
			}
			if (this.cancelled) {
				this.release()
				this.state = 'idle'
				return
			}
			try {
				const recorder = new window.MediaRecorder(this.stream)
				recorder.ondataavailable = event => { if (event.data?.size) this.chunks.push(event.data) }
				recorder.onstop = () => this.onStop(recorder.mimeType)
				recorder.start()
				this.recorder = recorder
			}
			catch {
				// the recorder never ran, so no onstop: free the microphone here
				this.recorder = null
				this.release()
				this.state = 'idle'
				this.error = this.$t('text.copilotMicErrorUnsupported')
				return
			}
			this.startedAt = Date.now()
			this.elapsed = 0
			this.state = 'recording'
			this.timer = setInterval(this.tick, TICK_MS)
		},
		tick() {
			this.elapsed = (Date.now() - this.startedAt) / 1000
			if (this.elapsed >= this.maxSeconds) {
				this.elapsed = this.maxSeconds
				this.stop()
			}
		},
		stop() {
			if (this.recorder?.state === 'recording') this.recorder.stop()
		},
		cancel() {
			this.cancelled = true
			if (this.recorder?.state === 'recording') {
				this.recorder.stop()
			}
			else {
				this.release()
				this.state = 'idle'
			}
		},
		// the recorder's onstop: the microphone is free, the audio goes to the service once
		async onStop(mimeType) {
			this.release()
			const blob = new Blob(this.chunks, { type: mimeType || 'audio/webm' })
			this.chunks = []
			this.recorder = null
			if (this.cancelled) {
				this.state = 'idle'
				return
			}
			this.state = 'transcribing'
			try {
				const text = (await this.transcribe(blob)) ?? ''
				if (!text.trim()) throw Object.assign(new Error('empty'), { empty: true })
				this.state = 'idle'
				this.$emit('transcript', text.trim())
			}
			catch (error) {
				this.state = 'idle'
				this.error = this.$t(
					error?.empty ? 'text.copilotMicErrorEmpty'
					: error?.status === 413 ? 'text.copilotMicErrorTooLong'
					: error?.status === 503 ? 'text.copilotMicErrorOff'
					: 'text.copilotMicErrorFailed',
				)
			}
		},
		release() {
			clearInterval(this.timer)
			this.timer = null
			this.stream?.getTracks().forEach(track => track.stop())
			this.stream = null
			document.removeEventListener('keydown', this.onEscape, true)
		},
	},
}
</script>

<style scoped lang="scss">
.CopilotMic {
	display: inline-flex;
	align-items: center;
	gap: 6px;

	&__time {
		font-variant-numeric: tabular-nums;
		font-size: 12px;
		color: #c62828;
	}
	&__error {
		font-size: 12px;
		color: #c62828;
		max-width: 180px;
	}
	&__spinner {
		margin: 0 8px;
	}
	&__button--pulse {
		animation: CopilotMic-pulse 1.2s ease-in-out infinite;
	}
}

@keyframes CopilotMic-pulse {
	0%, 100% { opacity: 1; }
	50% { opacity: 0.45; }
}
</style>
